import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import { PageLayout, PageHeader, ContentSection } from '../components/layout/PageLayout';
import { useSelector, useDispatch } from 'react-redux';
import type { AppDispatch } from '../store/store';
import { fetchFlips, selectAllFlips, selectFlipsStatus, selectFlipsError } from '../store/slices/flipsSlice';
import type { Flip } from '../services/flipsService';
import FlipDetailPanel from '../components/flip/FlipDetailPanel';

const fmt = (n: number): string => (n ?? 0).toLocaleString(undefined, { maximumFractionDigits: 2 });

const FlipsPage: React.FC = () => {
  const dispatch: AppDispatch = useDispatch();
  const flips = useSelector(selectAllFlips);
  const status = useSelector(selectFlipsStatus);
  const error = useSelector(selectFlipsError);
  const [detailsPanelOpen, setDetailsPanelOpen] = useState(false);
  const [selectedFlip, setSelectedFlip] = useState<Flip | null>(null);

  useEffect(() => {
    dispatch(fetchFlips());
  }, [dispatch]);

  const handleFlipClick = (flip: Flip) => {
    setSelectedFlip(flip);
    setDetailsPanelOpen(true);
  };

  const handleCloseDetailsPanel = () => {
    setDetailsPanelOpen(false);
    setSelectedFlip(null);
  };

  return (
    <PageLayout>
      <PageHeader title='Flips' subtitle='Completed currency flips by character' />
      <ContentSection>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
          <Button startIcon={<RefreshIcon />} onClick={() => dispatch(fetchFlips())} disabled={status === 'loading'}>
            Refresh
          </Button>
        </Box>
        {status === 'loading' && flips.length === 0 && (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        )}
        {status === 'failed' && (
          <Alert severity='error' sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        {status === 'succeeded' && flips.length === 0 && <Alert severity='info'>No flips recorded yet.</Alert>}
        {flips.length > 0 && (
          <TableContainer>
            <Table size='small'>
              <TableHead>
                <TableRow>
                  <TableCell>Character</TableCell>
                  <TableCell>When</TableCell>
                  <TableCell>Cycle</TableCell>
                  <TableCell align='right'>Chaos in</TableCell>
                  <TableCell align='right'>Gold in</TableCell>
                  <TableCell align='right'>Chaos out</TableCell>
                  <TableCell align='right'>Net</TableCell>
                  <TableCell>Result</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {flips.map(flip => {
                  const path = flip.path ?? [];
                  const cycleText = path.length > 0 ? path.join(' -> ') : 'no plan recorded';
                  const truncated = cycleText.length > 60 ? `${cycleText.slice(0, 57)}...` : cycleText;
                  const net = flip.netSource * flip.chaosRate;
                  return (
                    <TableRow
                      key={flip.id}
                      hover
                      onClick={() => handleFlipClick(flip)}
                      sx={{ cursor: 'pointer', '&:last-child td, &:last-child th': { border: 0 } }}
                    >
                      <TableCell>{flip.characterName}</TableCell>
                      <TableCell>{new Date(flip.flippedAt).toLocaleString()}</TableCell>
                      <TableCell>{truncated}</TableCell>
                      <TableCell align='right'>{fmt(flip.commitQty * flip.chaosRate)}</TableCell>
                      <TableCell align='right'>{fmt(flip.goldSpent)}</TableCell>
                      <TableCell align='right'>{fmt(flip.sourceRecovered * flip.chaosRate)}</TableCell>
                      <TableCell align='right' sx={{ color: net >= 0 ? 'success.main' : 'error.main', fontWeight: 600 }}>
                        {fmt(net)}
                      </TableCell>
                      <TableCell>
                        <Typography
                          variant='body2'
                          sx={{
                            color: flip.result === 'done' ? 'success.main' : 'warning.main',
                            textTransform: 'capitalize',
                          }}
                        >
                          {flip.result}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </ContentSection>
      <FlipDetailPanel open={detailsPanelOpen} onClose={handleCloseDetailsPanel} flip={selectedFlip} />
    </PageLayout>
  );
};

export default FlipsPage;
